// Settings modal — keyboard rebind + misc. Accessible from the pause
// menu `⚙ SETTINGS` button. Uses the central keybinds store.

import {
  ACTION_ORDER,
  ACTION_LABELS,
  ACTION_ICONS,
  DEFAULT_BINDINGS,
  getBindings,
  setPrimary,
  resetToDefaults,
  formatKeyCode,
  isModifierOnly,
  type KeyAction,
} from '../settings/keybinds';
import { THEMES, getActiveTheme, setActiveTheme } from '../theme/themes';
// Art-pack picker removed 2026-04-20 — game ships a single definitive
// style (roguelike). `setActivePack` import dropped; if a legacy URL
// carries `?pack=<id>`, `getActivePack()` ignores unknown ids and falls
// back to the flagship.
// DEBUG section is dev-only — dynamic-imported below when
// `import.meta.env.DEV` is true, tree-shaken otherwise. No static
// import here so the prod bundle never sees it.
import { HUD_TOGGLES, isHudVisible, setHudVisible } from './hud_visibility';
// `PALETTE` was used by the old pack-preview helpers; no longer needed.
import {
  SFX,
  SONGS,
  setMusicVolume,
  setSfxVolume,
  getMusicVolume,
  getSfxVolume,
  resumeAudio,
  testBeep,
  audioDiag,
  type SongId,
} from '../audio/synth';

const NAME_KEY = 'smp.username';
const INTRO_KEY = 'smp.introSeenV1';

export type SettingsCallbacks = {
  onReplayIntro?: () => void;
};

export function openSettings(
  callbacks: SettingsCallbacks = {},
  onClose?: () => void,
): () => void {
  // Opening settings is itself a user gesture — kick the AudioContext
  // out of suspended so any volume/preview tweaks the user makes
  // actually take audible effect immediately.
  resumeAudio();
  console.info('[settings] open — audio diag:', audioDiag());
  const overlay = document.createElement('div');
  overlay.className = 'ui interactive';
  overlay.style.cssText = `
    position: fixed; inset: 0;
    background: rgba(2,6,23,.93);
    z-index: 9999;
    display: flex; flex-direction: column;
    align-items: center; justify-content: flex-start;
    color: #e5e7eb;
    font: 700 14px system-ui, sans-serif;
    padding: 32px 20px;
    overflow-y: auto;
  `;

  const title = document.createElement('div');
  title.style.cssText = `
    font: 900 28px system-ui; letter-spacing: 4px; margin-bottom: 4px;
    color: #fde047;
    text-shadow: 0 0 18px rgba(253,224,71,.35);
  `;
  title.textContent = '⚙ SETTINGS';
  overlay.appendChild(title);

  const sub = document.createElement('div');
  sub.style.cssText = `opacity:.65;font-size:11px;letter-spacing:2px;margin-bottom:26px;`;
  sub.textContent = 'AUDIO · KEYBOARD · PROFILE · VISUALS · HELP';
  overlay.appendChild(sub);

  // ---- Toast (shared) ----
  const toast = document.createElement('div');
  toast.style.cssText = `
    position: absolute; top: 90px; left: 50%;
    transform: translateX(-50%);
    padding: 8px 18px;
    background: rgba(15,23,42,.9);
    border: 1px solid #fde047;
    border-radius: 10px;
    font: 800 12px system-ui; letter-spacing: 1.5px;
    color: #fde047;
    opacity: 0; pointer-events: none;
    transition: opacity .2s;
  `;
  overlay.appendChild(toast);
  let toastTimer = 0;
  function flashToast(msg: string, color = '#fde047') {
    toast.textContent = msg;
    toast.style.color = color;
    toast.style.borderColor = color;
    toast.style.opacity = '1';
    clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => { toast.style.opacity = '0'; }, 1800);
  }

  // ---- Section: THEMES ----
  const themesSection = document.createElement('div');
  themesSection.style.cssText = `
    width: 560px; max-width: 92vw;
    background: rgba(15,23,42,.6);
    border: 1px solid #475569;
    border-radius: 14px;
    padding: 18px 22px;
    margin-bottom: 20px;
  `;
  const themesTitle = document.createElement('div');
  themesTitle.style.cssText = `
    font: 900 11px system-ui; letter-spacing: 2.5px;
    color: #94a3b8; margin-bottom: 12px;
  `;
  themesTitle.textContent = 'THEMES';
  themesSection.appendChild(themesTitle);

  const themeGrid = document.createElement('div');
  themeGrid.style.cssText = `
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 10px;
  `;
  const activeThemeId = getActiveTheme().id;
  const overlayIcon: Record<string, string> = {
    scanlines: '▦',
    halftone: '⋮⋮',
    posterize: '◩',
    grain: '░',
  };
  for (const t of THEMES) {
    const card = document.createElement('button');
    const isActive = t.id === activeThemeId;
    card.style.cssText = `
      position: relative;
      display: flex; flex-direction: column; gap: 6px;
      padding: 10px 10px 12px;
      background: rgba(30,41,59,.55);
      border: 2px solid ${isActive ? '#fde047' : 'rgba(71,85,105,.5)'};
      border-radius: 10px;
      cursor: pointer;
      text-align: left;
      font: inherit; color: #e5e7eb;
      transition: transform .12s, border-color .12s;
    `;
    card.addEventListener('mouseenter', () => {
      if (!isActive) card.style.borderColor = '#94a3b8';
      card.style.transform = 'translateY(-2px)';
    });
    card.addEventListener('mouseleave', () => {
      card.style.borderColor = isActive ? '#fde047' : 'rgba(71,85,105,.5)';
      card.style.transform = '';
    });

    const swatch = document.createElement('div');
    swatch.style.cssText = `
      display: flex; height: 28px; border-radius: 5px; overflow: hidden;
      border: 1px solid rgba(255,255,255,.08);
    `;
    const hex = (n: number) => '#' + n.toString(16).padStart(6, '0');
    const virginStripe = document.createElement('div');
    virginStripe.style.cssText = `flex:1; background:${hex(t.tintVirgin)};`;
    const midStripe = document.createElement('div');
    midStripe.style.cssText = `width:4px; background:${hex(t.midline)};`;
    const infStripe = document.createElement('div');
    infStripe.style.cssText = `flex:1; background:${hex(t.tintChad)};`;
    swatch.appendChild(virginStripe);
    swatch.appendChild(midStripe);
    swatch.appendChild(infStripe);

    const name = document.createElement('div');
    name.style.cssText = `
      font: 900 12px system-ui; letter-spacing: 1px;
      color: #f1f5f9;
    `;
    name.textContent = t.name.toUpperCase();

    const tagline = document.createElement('div');
    tagline.style.cssText = `
      font: 600 10px system-ui; letter-spacing: .3px;
      color: #94a3b8; line-height: 1.3;
    `;
    tagline.textContent = t.tagline;

    card.appendChild(swatch);
    card.appendChild(name);
    card.appendChild(tagline);

    if (t.overlay) {
      const badge = document.createElement('div');
      badge.style.cssText = `
        position: absolute; top: 6px; right: 8px;
        padding: 2px 6px;
        background: rgba(15,23,42,.8);
        border: 1px solid #475569;
        border-radius: 4px;
        font: 800 9px system-ui; letter-spacing: .5px;
        color: #cbd5e1;
      `;
      badge.textContent = (overlayIcon[t.overlay] ?? '◆') + ' ' + t.overlay.toUpperCase();
      card.appendChild(badge);
    }

    if (isActive) {
      const check = document.createElement('div');
      check.style.cssText = `
        position: absolute; top: 6px; left: 8px;
        width: 18px; height: 18px; line-height: 18px;
        background: #fde047; color: #0f172a;
        border-radius: 50%;
        text-align: center;
        font: 900 12px system-ui;
      `;
      check.textContent = '✓';
      card.appendChild(check);
    }

    card.addEventListener('click', () => {
      if (t.id === activeThemeId) return;
      setActiveTheme(t.id);
      try {
        const url = new URL(location.href);
        url.searchParams.set('theme', t.id);
        history.replaceState(null, '', url.toString());
      } catch { /* ignore */ }
      flashToast(`LOADING ${t.name.toUpperCase()}…`, '#86efac');
      setTimeout(() => location.reload(), 500);
    });

    themeGrid.appendChild(card);
  }
  themesSection.appendChild(themeGrid);
  overlay.appendChild(themesSection);

  // ---- Section: AUDIO ----
  const audioSection = document.createElement('div');
  audioSection.style.cssText = `
    width: 560px; max-width: 92vw;
    background: rgba(15,23,42,.6);
    border: 1px solid #475569;
    border-radius: 14px;
    padding: 18px 22px;
    margin-bottom: 20px;
  `;
  const audioTitle = document.createElement('div');
  audioTitle.style.cssText = `
    font: 900 11px system-ui; letter-spacing: 2.5px;
    color: #94a3b8; margin-bottom: 12px;
    display: flex; align-items: center; justify-content: space-between;
  `;
  audioTitle.innerHTML = `<span>AUDIO</span><span style="font:700 9px system-ui;color:#64748b;letter-spacing:1.5px;">VOLUME · SOUNDTRACK</span>`;
  audioSection.appendChild(audioTitle);

  // Slider helper — builds a labeled range input with a live percent
  // readout that calls back on input. Returns the wrapper element.
  function buildVolumeRow(
    label: string,
    initial: number,
    onChange: (v: number) => void,
  ): HTMLDivElement {
    const row = document.createElement('div');
    row.style.cssText = `
      display: flex; align-items: center; gap: 14px;
      padding: 8px 4px;
      font: 800 12px system-ui; letter-spacing: .5px;
    `;
    const lbl = document.createElement('div');
    lbl.style.cssText = `width: 100px; color: #cbd5e1;`;
    lbl.textContent = label;
    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = '0';
    slider.max = '100';
    slider.value = String(Math.round(initial * 100));
    slider.style.cssText = `
      flex: 1; accent-color: #fde047; cursor: pointer;
    `;
    const readout = document.createElement('div');
    readout.style.cssText = `
      width: 48px; text-align: right;
      font: 800 12px system-ui; color: #fde047;
    `;
    readout.textContent = `${slider.value}%`;
    slider.addEventListener('input', () => {
      const v = parseInt(slider.value, 10) / 100;
      readout.textContent = `${slider.value}%`;
      onChange(v);
    });
    row.appendChild(lbl);
    row.appendChild(slider);
    row.appendChild(readout);
    return row;
  }

  audioSection.appendChild(buildVolumeRow('Music', getMusicVolume(), setMusicVolume));
  audioSection.appendChild(buildVolumeRow('SFX',   getSfxVolume(),   setSfxVolume));

  // Diagnostic: Test Beep + Log Audio Diag buttons. Dev-only — gated
  // on the build-time `import.meta.env.DEV` flag. Prod builds strip
  // this entire block.
  const debugAudio = import.meta.env.DEV;
  const testRow = document.createElement('div');
  testRow.style.cssText = `
    display: flex; align-items: center; gap: 12px;
    padding: 6px 4px;
  `;
  const testBtn = document.createElement('button');
  testBtn.style.cssText = `
    padding: 8px 16px;
    background: rgba(253,224,71,.12);
    border: 1px solid #fde047;
    border-radius: 8px;
    color: #fde047;
    font: 800 11px system-ui; letter-spacing: 1px;
    cursor: pointer;
  `;
  testBtn.textContent = '🔊 TEST BEEP';
  testBtn.addEventListener('click', () => {
    resumeAudio();
    testBeep();
    console.info('[settings] testBeep clicked — diag:', audioDiag());
    flashToast('TEST BEEP FIRED', '#fde047');
  });
  const diagBtn = document.createElement('button');
  diagBtn.style.cssText = `
    padding: 8px 16px;
    background: rgba(15,23,42,.8);
    border: 1px solid #475569;
    border-radius: 8px;
    color: #cbd5e1;
    font: 800 11px system-ui; letter-spacing: 1px;
    cursor: pointer;
  `;
  diagBtn.textContent = '🩺 LOG AUDIO DIAG';
  diagBtn.addEventListener('click', () => {
    const d = audioDiag();
    console.info('[audio diag]', d);
    flashToast('DIAG IN CONSOLE', '#94a3b8');
  });
  testRow.appendChild(testBtn);
  testRow.appendChild(diagBtn);
  if (debugAudio) audioSection.appendChild(testRow);

  // Tempo multiplier — type a number like 1.0, 1.5, 0.75 to scale the
  // active song's BPM live. Used for dialing in the right feel without
  // editing songs.ts and reloading.
  const tempoRow = document.createElement('div');
  tempoRow.style.cssText = `
    display: flex; align-items: center; gap: 14px;
    padding: 8px 4px;
    font: 800 12px system-ui; letter-spacing: .5px;
  `;
  const tempoLbl = document.createElement('div');
  tempoLbl.style.cssText = `width: 100px; color: #cbd5e1;`;
  tempoLbl.textContent = 'Tempo ×';
  const tempoInput = document.createElement('input');
  tempoInput.type = 'number';
  tempoInput.min = '0.25';
  tempoInput.max = '4';
  tempoInput.step = '0.05';
  tempoInput.value = String(SFX.music.getTempoScale());
  tempoInput.style.cssText = `
    width: 90px; padding: 6px 10px;
    background: rgba(15,23,42,.8);
    border: 1px solid #475569;
    border-radius: 8px;
    color: #fde047;
    font: 800 13px system-ui; letter-spacing: .5px;
    outline: none;
  `;
  const tempoHint = document.createElement('div');
  tempoHint.style.cssText = `
    flex: 1; font: 600 10px system-ui; color: #64748b; letter-spacing: .3px;
  `;
  function refreshTempoHint() {
    const scale = parseFloat(tempoInput.value);
    if (!isFinite(scale) || scale <= 0) {
      tempoHint.textContent = 'enter a positive number (0.25–4)';
      tempoHint.style.color = '#ef4444';
      return;
    }
    // Show resulting BPM for each song so the player sees the math.
    const bpms = (Object.keys(SONGS) as SongId[])
      .map(id => `${SONGS[id].bpm * scale | 0}`)
      .join(' / ');
    tempoHint.textContent = `→ ${bpms} BPM (plaza/classic/tdm/pve/race)`;
    tempoHint.style.color = '#64748b';
  }
  tempoInput.addEventListener('input', () => {
    const v = parseFloat(tempoInput.value);
    if (isFinite(v) && v > 0) SFX.music.setTempoScale(v);
    refreshTempoHint();
  });
  refreshTempoHint();
  tempoRow.appendChild(tempoLbl);
  tempoRow.appendChild(tempoInput);
  tempoRow.appendChild(tempoHint);
  audioSection.appendChild(tempoRow);

  // Soundtrack preview row — one button per song. Clicking any button
  // calls SFX.music.previewSong which suppresses the per-frame
  // auto-selector for ~12s so the preview isn't immediately overridden.
  const songLabel = document.createElement('div');
  songLabel.style.cssText = `
    margin-top: 14px;
    font: 900 10px system-ui; letter-spacing: 2px;
    color: #94a3b8;
  `;
  songLabel.textContent = 'PREVIEW SOUNDTRACK';
  audioSection.appendChild(songLabel);

  const songRow = document.createElement('div');
  songRow.style.cssText = `
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
    gap: 8px;
    margin-top: 8px;
  `;
  const SONG_LABELS: Record<SongId, { name: string; tagline: string; tint: string }> = {
    plaza:   { name: 'Plaza',   tagline: 'Lobby · F maj', tint: '#fde047' },
    classic: { name: 'Classic', tagline: 'A min · 110',   tint: '#60a5fa' },
    tdm:     { name: 'TDM',     tagline: 'D min · 124',   tint: '#fb923c' },
    pve:     { name: 'PvE',     tagline: 'Phrygian · 78', tint: '#a78bfa' },
    race:    { name: 'Race',    tagline: 'E maj · 140',   tint: '#34d399' },
  };
  for (const id of Object.keys(SONGS) as SongId[]) {
    const meta = SONG_LABELS[id];
    const btn = document.createElement('button');
    btn.style.cssText = `
      display: flex; flex-direction: column; gap: 2px;
      padding: 8px 6px;
      background: rgba(30,41,59,.55);
      border: 1px solid rgba(71,85,105,.5);
      border-radius: 8px;
      cursor: pointer;
      font: inherit; color: #e5e7eb;
      transition: transform .12s, border-color .12s;
    `;
    btn.addEventListener('mouseenter', () => {
      btn.style.borderColor = meta.tint;
      btn.style.transform = 'translateY(-1px)';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.borderColor = 'rgba(71,85,105,.5)';
      btn.style.transform = '';
    });
    const n = document.createElement('div');
    n.style.cssText = `font: 900 12px system-ui; letter-spacing: .5px; color:${meta.tint};`;
    n.textContent = '▶ ' + meta.name.toUpperCase();
    const tag = document.createElement('div');
    tag.style.cssText = `font: 600 9px system-ui; color: #94a3b8; letter-spacing: .3px;`;
    tag.textContent = meta.tagline;
    btn.appendChild(n);
    btn.appendChild(tag);
    btn.addEventListener('click', () => {
      SFX.music.previewSong(id, 15000);
      flashToast(`PREVIEW: ${meta.name.toUpperCase()}`, meta.tint);
    });
    songRow.appendChild(btn);
  }
  audioSection.appendChild(songRow);

  const previewHint = document.createElement('div');
  previewHint.style.cssText = `
    margin-top: 10px;
    font: 600 10px system-ui; color: #64748b; letter-spacing: .3px;
  `;
  previewHint.textContent = 'Preview holds for 15 seconds, then auto-selection resumes.';
  audioSection.appendChild(previewHint);

  overlay.appendChild(audioSection);

  // ---- Section: KEYBOARD ----
  const kbSection = document.createElement('div');
  kbSection.style.cssText = `
    width: 560px; max-width: 92vw;
    background: rgba(15,23,42,.6);
    border: 1px solid #475569;
    border-radius: 14px;
    padding: 18px 22px;
    margin-bottom: 20px;
  `;
  const kbTitle = document.createElement('div');
  kbTitle.style.cssText = `
    font: 900 11px system-ui; letter-spacing: 2.5px;
    color: #94a3b8; margin-bottom: 12px;
  `;
  kbTitle.textContent = 'KEYBOARD';
  kbSection.appendChild(kbTitle);

  const rowsContainer = document.createElement('div');
  rowsContainer.style.cssText = `display: flex; flex-direction: column; gap: 4px;`;
  kbSection.appendChild(rowsContainer);

  let listening: { action: KeyAction; row: HTMLDivElement } | null = null;

  function renderRows() {
    rowsContainer.innerHTML = '';
    const bindings = getBindings();
    for (const action of ACTION_ORDER) {
      const row = document.createElement('div');
      row.style.cssText = `
        display: flex; align-items: center; gap: 14px;
        padding: 9px 12px;
        border-radius: 8px;
        background: rgba(30,41,59,.5);
        border: 1px solid rgba(71,85,105,.3);
      `;
      const icon = document.createElement('div');
      icon.style.cssText = `width: 24px; text-align: center; font-size: 18px;`;
      icon.textContent = ACTION_ICONS[action];
      const label = document.createElement('div');
      label.style.cssText = `flex: 1; font: 800 12px system-ui; letter-spacing: .5px;`;
      label.textContent = ACTION_LABELS[action];

      const keys = bindings[action];
      const primary = keys[0] ?? '';
      const secondary = keys[1] ?? '';

      const keyBadge = (code: string, dim = false) => {
        const b = document.createElement('div');
        b.style.cssText = `
          min-width: 54px;
          padding: 5px 10px;
          background: ${dim ? 'rgba(71,85,105,.35)' : 'rgba(253,224,71,.15)'};
          border: 1px solid ${dim ? '#475569' : '#fde047'};
          border-radius: 6px;
          font: 800 11px system-ui; letter-spacing: 1px;
          color: ${dim ? '#94a3b8' : '#fde047'};
          text-align: center;
        `;
        b.textContent = code ? formatKeyCode(code) : '—';
        return b;
      };

      const primaryBadge = keyBadge(primary);
      const secondaryBadge = keyBadge(secondary, true);

      const rebindBtn = document.createElement('button');
      rebindBtn.style.cssText = `
        padding: 6px 14px;
        background: rgba(15,23,42,.8);
        border: 1px solid #475569;
        border-radius: 8px;
        font: 800 11px system-ui; letter-spacing: 1px;
        color: #e5e7eb;
        cursor: pointer;
      `;
      rebindBtn.textContent = 'REBIND';
      rebindBtn.addEventListener('click', () => startListening(action, row));

      row.appendChild(icon);
      row.appendChild(label);
      row.appendChild(primaryBadge);
      row.appendChild(secondaryBadge);
      row.appendChild(rebindBtn);
      rowsContainer.appendChild(row);
    }
  }

  function startListening(action: KeyAction, row: HTMLDivElement) {
    if (listening) return;
    listening = { action, row };
    row.style.background = 'rgba(253,224,71,.12)';
    row.style.borderColor = '#fde047';
    const rebindBtn = row.querySelector<HTMLButtonElement>('button')!;
    rebindBtn.textContent = 'PRESS A KEY…';
    rebindBtn.style.color = '#fde047';
  }

  function cancelListening() {
    if (!listening) return;
    listening = null;
    renderRows();
  }

  // Global capture while listening
  const onCapture = (e: KeyboardEvent) => {
    if (!listening) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.code === 'Escape') {
      cancelListening();
      flashToast('REBIND CANCELED', '#94a3b8');
      return;
    }
    if (isModifierOnly(e.code)) {
      flashToast('MODIFIERS ARE NOT BINDABLE', '#ef4444');
      return;
    }
    const action = listening.action;
    const { reassignedFrom } = setPrimary(action, e.code);
    listening = null;
    renderRows();
    if (reassignedFrom) {
      flashToast(`REASSIGNED FROM ${ACTION_LABELS[reassignedFrom].toUpperCase()}`, '#fb923c');
    } else {
      flashToast(`BOUND ${formatKeyCode(e.code)}`, '#86efac');
    }
  };
  addEventListener('keydown', onCapture, true);

  // Reset button
  const resetBtn = document.createElement('button');
  resetBtn.style.cssText = `
    margin-top: 14px; padding: 7px 16px;
    background: rgba(15,23,42,.8);
    border: 1px solid #475569;
    border-radius: 8px;
    color: #94a3b8;
    font: 800 11px system-ui; letter-spacing: 1.5px;
    cursor: pointer;
  `;
  resetBtn.textContent = '↺ RESET TO DEFAULTS';
  resetBtn.addEventListener('click', () => {
    resetToDefaults();
    renderRows();
    flashToast('BINDINGS RESET', '#86efac');
  });
  kbSection.appendChild(resetBtn);

  overlay.appendChild(kbSection);
  renderRows();

  // ---- Section: PROFILE ----
  const profileSection = document.createElement('div');
  profileSection.style.cssText = `
    width: 560px; max-width: 92vw;
    background: rgba(15,23,42,.6);
    border: 1px solid #475569;
    border-radius: 14px;
    padding: 18px 22px;
    margin-bottom: 20px;
  `;
  const profTitle = document.createElement('div');
  profTitle.style.cssText = `
    font: 900 11px system-ui; letter-spacing: 2.5px;
    color: #94a3b8; margin-bottom: 12px;
  `;
  profTitle.textContent = 'PROFILE';
  profileSection.appendChild(profTitle);

  const profileRow = document.createElement('div');
  profileRow.style.cssText = `display: flex; align-items: center; gap: 14px;`;
  const currentName = document.createElement('div');
  currentName.style.cssText = `flex: 1; font: 800 12px system-ui; letter-spacing: .5px;`;
  const saved = localStorage.getItem(NAME_KEY) ?? '(auto)';
  currentName.textContent = `Name: ${saved}`;

  const nameInput = document.createElement('input');
  nameInput.type = 'text';
  nameInput.maxLength = 16;
  nameInput.placeholder = 'New name';
  nameInput.style.cssText = `
    width: 160px; padding: 7px 12px;
    background: rgba(15,23,42,.8);
    border: 1px solid #475569;
    border-radius: 8px;
    color: #e5e7eb;
    font: 700 12px system-ui;
    outline: none;
  `;

  const saveNameBtn = document.createElement('button');
  saveNameBtn.style.cssText = `
    padding: 7px 16px;
    background: #3b82f6;
    border: 0;
    border-radius: 8px;
    color: #fff;
    font: 800 11px system-ui; letter-spacing: 1px;
    cursor: pointer;
  `;
  saveNameBtn.textContent = 'SAVE & RELOAD';
  saveNameBtn.addEventListener('click', () => {
    const trimmed = nameInput.value.trim().slice(0, 16);
    if (trimmed.length === 0) {
      flashToast('NAME CANNOT BE EMPTY', '#ef4444');
      return;
    }
    localStorage.setItem(NAME_KEY, trimmed);
    flashToast('RELOADING…', '#86efac');
    setTimeout(() => location.reload(), 500);
  });

  profileRow.appendChild(currentName);
  profileRow.appendChild(nameInput);
  profileRow.appendChild(saveNameBtn);
  profileSection.appendChild(profileRow);
  overlay.appendChild(profileSection);

  // ---- Section: VISUALS ----
  const visualsSection = document.createElement('div');
  visualsSection.style.cssText = `
    width: 560px; max-width: 92vw;
    background: rgba(15,23,42,.6);
    border: 1px solid #475569;
    border-radius: 14px;
    padding: 18px 22px;
    margin-bottom: 20px;
  `;
  const visualsTitle = document.createElement('div');
  visualsTitle.style.cssText = `
    font: 900 11px system-ui; letter-spacing: 2.5px;
    color: #94a3b8; margin-bottom: 12px;
  `;
  visualsTitle.textContent = 'VISUALS';
  visualsSection.appendChild(visualsTitle);

  const allyRingsRow = document.createElement('label');
  allyRingsRow.style.cssText = `
    display: flex; align-items: center; gap: 12px;
    cursor: pointer;
    font: 700 12px system-ui; letter-spacing: .5px;
  `;
  const allyRingsCheckbox = document.createElement('input');
  allyRingsCheckbox.type = 'checkbox';
  allyRingsCheckbox.checked = localStorage.getItem('smp.showAllyRings') === '1';
  allyRingsCheckbox.style.cssText = `
    width: 18px; height: 18px; accent-color: #fde047; cursor: pointer;
  `;
  allyRingsCheckbox.addEventListener('change', () => {
    localStorage.setItem(
      'smp.showAllyRings',
      allyRingsCheckbox.checked ? '1' : '0',
    );
    flashToast(
      allyRingsCheckbox.checked ? 'ALLY RINGS ON' : 'ALLY RINGS OFF',
      '#fde047',
    );
  });
  const allyRingsText = document.createElement('span');
  allyRingsText.innerHTML = `Show ally weapon rings when hit
    <em style="opacity:.55; font-weight:600; letter-spacing:0; font-size:10px;">
      (not recommended — visual noise)
    </em>`;
  allyRingsRow.appendChild(allyRingsCheckbox);
  allyRingsRow.appendChild(allyRingsText);
  visualsSection.appendChild(allyRingsRow);
  overlay.appendChild(visualsSection);

  // ---- Section: HUD ----
  // Per-element on/off toggles for every overlay that competes for screen
  // real estate. Defaults match the as-shipped HUD; turning anything off
  // immediately hides it via the smp:hud-toggle event (no reload).
  const hudSection = document.createElement('div');
  hudSection.style.cssText = `
    width: 560px; max-width: 92vw;
    background: rgba(15,23,42,.6);
    border: 1px solid #475569;
    border-radius: 14px;
    padding: 18px 22px;
    margin-bottom: 20px;
  `;
  const hudTitle = document.createElement('div');
  hudTitle.style.cssText = `
    font: 900 11px system-ui; letter-spacing: 2.5px;
    color: #94a3b8; margin-bottom: 12px;
  `;
  hudTitle.textContent = 'HUD ELEMENTS';
  hudSection.appendChild(hudTitle);

  for (const t of HUD_TOGGLES) {
    const row = document.createElement('label');
    row.style.cssText = `
      display: flex; align-items: center; gap: 10px;
      padding: 6px 4px;
      font: 700 12px system-ui; color: #cbd5e1;
      cursor: pointer;
    `;
    const cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.checked = isHudVisible(t.key);
    cb.style.cssText = 'width:16px;height:16px;accent-color:#fde047;cursor:pointer;';
    cb.addEventListener('change', () => {
      setHudVisible(t.key, cb.checked);
      flashToast(`${t.label.toUpperCase()} ${cb.checked ? 'ON' : 'OFF'}`, '#fde047');
    });
    const label = document.createElement('span');
    label.innerHTML = `${t.label}${t.hint ? `
      <em style="opacity:.55;font-weight:600;letter-spacing:0;font-size:10px;">
        — ${t.hint}
      </em>` : ''}`;
    row.appendChild(cb);
    row.appendChild(label);
    hudSection.appendChild(row);
  }
  overlay.appendChild(hudSection);

  // ---- Section: HELP ----
  const helpSection = document.createElement('div');
  helpSection.style.cssText = `
    width: 560px; max-width: 92vw;
    background: rgba(15,23,42,.6);
    border: 1px solid #475569;
    border-radius: 14px;
    padding: 18px 22px;
    margin-bottom: 20px;
  `;
  const helpTitle = document.createElement('div');
  helpTitle.style.cssText = `
    font: 900 11px system-ui; letter-spacing: 2.5px;
    color: #94a3b8; margin-bottom: 12px;
  `;
  helpTitle.textContent = 'HELP';
  helpSection.appendChild(helpTitle);

  const replayBtn = document.createElement('button');
  replayBtn.style.cssText = `
    padding: 9px 18px;
    background: rgba(15,23,42,.8);
    border: 1px solid #475569;
    border-radius: 8px;
    color: #e5e7eb;
    font: 800 12px system-ui; letter-spacing: 1px;
    cursor: pointer;
  `;
  replayBtn.textContent = '▶ REPLAY INTRO TUTORIAL';
  replayBtn.addEventListener('click', () => {
    localStorage.removeItem(INTRO_KEY);
    close();
    callbacks.onReplayIntro?.();
  });
  helpSection.appendChild(replayBtn);

  overlay.appendChild(helpSection);

  // Section: DEBUG — dev-only. Dynamic-imported so the section code
  // (and the `isDebugEnabled`/`setDebugEnabled` localStorage toggle)
  // never ship to prod. See src/dev/settings_debug_section.ts.
  if (import.meta.env.DEV) {
    import('../dev/settings_debug_section').then(({ buildDebugSection }) => {
      buildDebugSection(overlay, { close, flashToast });
    });
  }

  // ---- Close button ----
  const closeBtn = document.createElement('button');
  closeBtn.textContent = 'CLOSE [ESC]';
  closeBtn.style.cssText = `
    margin-top: 6px; padding: 9px 24px;
    border-radius: 8px;
    border: 1px solid #475569;
    background: rgba(15,23,42,.85);
    color: #e5e7eb;
    cursor: pointer;
    font: 800 12px system-ui; letter-spacing: 1.5px;
  `;
  overlay.appendChild(closeBtn);

  let closed = false;
  function close() {
    if (closed) return;
    closed = true;
    removeEventListener('keydown', onCapture, true);
    removeEventListener('keydown', onKeyTop);
    clearTimeout(toastTimer);
    overlay.remove();
    onClose?.();
  }
  const onKeyTop = (e: KeyboardEvent) => {
    // Only let Escape close the modal if we're NOT mid-rebind —
    // the rebind capture handles its own Escape-cancel.
    if (listening) return;
    if (e.code === 'Escape') { e.preventDefault(); close(); }
  };
  addEventListener('keydown', onKeyTop);
  closeBtn.addEventListener('click', close);

  document.body.appendChild(overlay);
  return close;
}
